import { DestroyRef, effect, inject, signal } from '@angular/core';
import { catchError, EMPTY, Observable, Subject, takeUntil, tap } from 'rxjs';
import { TableRequestData } from '../models/table/table-request-param';
import { TablePage, TableResponse } from '../models/table-response';
import { Table, TablePageEvent } from 'primeng/table';
import { HttpUtils } from '../utils/http.utils';
import { PrimeFilterTarget, PrimeSortEvent } from '../models/custom-types';
import { DecoratorUtils } from '../utils/decorator.utils';
import { PaginatorState } from 'primeng/paginator';
import { SortDirection } from '../enums/sort-direction.enum';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpResponseService } from '../../core/services/http-error-response.service';

export abstract class TableBaseAbstractComponent<T> {
  readonly #destroyRef = inject(DestroyRef);
  readonly #httpResponseService = inject(HttpResponseService);
  protected rows = signal<T[]>([]);
  protected isDataLoading = signal(false);
  protected tablePage = signal<TablePage | undefined>(undefined);
  protected abstract tableRef: Table;
  protected abstract apiCall$: (
    params: TableRequestData,
  ) => Observable<TableResponse<T>>;
  protected tableRequest = signal<TableRequestData>({
    pageIndex: 0,
    pageSize: 10,
  });
  #cancelPreviousRequest$ = new Subject<void>();

  constructor() {
    this.#refreshRowsOnParamsChange();
  }

  #refreshRowsOnParamsChange(): void {
    effect(() => {
      this.rows.set([]);
      this.isDataLoading.set(true);
      this.#cancelPreviousRequest$.next();
      this.apiCall$(this.tableRequest())
        .pipe(
          takeUntil(this.#cancelPreviousRequest$),
          tap((response: TableResponse<T>) => {
            this.rows.set(response.content);

            const pageData = response.page ?? {
              size: (response as any).size,
              number: (response as any).number,
              totalElements: (response as any).totalElements,
              totalPages: (response as any).totalPages,
            };

            this.tablePage.set(pageData);
            this.isDataLoading.set(false);
          }),
          catchError((err) => {
            this.isDataLoading.set(false);
            this.#httpResponseService.showErrorMessage(
              err,
              'Wystąpił błąd podczas pobierania danych',
            );
            return EMPTY;
          }),
          takeUntilDestroyed(this.#destroyRef),
        )
        .subscribe();
    });
  }

  onPrimeSortChange(event: PrimeSortEvent): void {
    const normalizedOrder = event.order === 1 ? 1 : -1;

    this.tableRef.sortField = event.field;
    this.tableRef.sortOrder = normalizedOrder;

    this.tableRequest.update((tableRequestData: TableRequestData) => ({
      ...tableRequestData,
      sortBy: event.field,
      order: normalizedOrder === 1 ? SortDirection.ASC : SortDirection.DESC,
    }));
  }

  onPrimePageChange(event: TablePageEvent | PaginatorState): void {
    this.tableRequest.update((value) => {
      return {
        ...value,
        pageIndex: (event.first || 0) / (event.rows || 10),
        pageSize: event.rows || 10,
      };
    });
  }

  onPrimeFilterChange(target: PrimeFilterTarget, nazwa: string): void {
    let value: string | boolean | null = null;

    if (target instanceof Date) {
      value = HttpUtils.formatDateToLocalDateString(target);
    } else if (typeof target === 'boolean') {
      value = target;
    } else if (typeof target === 'string') {
      value = target;
    } else if (target && 'value' in (target as any)) {
      // HTMLInputElement | HTMLSelectElement
      value = (target as HTMLInputElement | HTMLSelectElement).value;
    }

    this.tableRequest.update((tableRequestData: TableRequestData) => ({
      ...tableRequestData,
      pageIndex: 0,
      filters: {
        ...tableRequestData.filters,
        [nazwa]: value,
      },
    }));
  }

  resetFilters(): void {
    this.tableRequest.update((tableRequestData: TableRequestData) => ({
      ...tableRequestData,
      pageIndex: 0,
      filters: {},
    }));
  }

  @DecoratorUtils.debounce(800)
  onPrimeFilterChangeMobile(target: PrimeFilterTarget, nazwa: string): void {
    this.onPrimeFilterChange(target, nazwa);
  }

  reloadData(): void {
    this.tableRequest.update((tableRequestData: TableRequestData) => ({
      ...tableRequestData,
    }));
  }
}
